import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavCtx = createContext({ ids: {}, toggle: async () => {}, has: () => false });

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState({});
  useEffect(() => { if (user) api.get("/favorites/ids").then(r => setIds(r.data)).catch(() => {}); else setIds({}); }, [user]);
  const has = useCallback((t, id) => (ids[t] || []).includes(id), [ids]);
  const toggle = useCallback(async (t, id) => {
    const on = !(ids[t] || []).includes(id);
    setIds(p => ({ ...p, [t]: on ? [...(p[t] || []), id] : (p[t] || []).filter(x => x !== id) }));
    try { const r = await api.post("/favorites/toggle", { target_type: t, target_id: id }); return r.data; }
    catch (e) { setIds(p => ({ ...p, [t]: !on ? [...(p[t] || []), id] : (p[t] || []).filter(x => x !== id) })); toast.error(e.response?.data?.detail || "Erro ao guardar"); }
  }, [ids]);
  const value = useMemo(() => ({ ids, has, toggle }), [ids, has, toggle]);
  return <FavCtx.Provider value={value}>{children}</FavCtx.Provider>;
}

export const useFavorites = () => useContext(FavCtx);

export function FavoriteButton({ type, id, count, size = "sm", className = "", testid, onToggled }) {
  const { user } = useAuth();
  const { has, toggle } = useFavorites();
  const nav = useNavigate(); const loc = useLocation();
  const [n, setN] = useState(count ?? null);
  const on = has(type, id);
  const click = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.info("Inicia sessão para guardar"); nav(`/login?next=${encodeURIComponent(loc.pathname)}`); return; }
    const r = await toggle(type, id);
    if (r && typeof r.count === "number") setN(r.count);
    onToggled?.(r);
  };
  const px = size === "lg" ? 18 : 14;
  return (
    <button type="button" onClick={click} aria-pressed={on} aria-label={on ? "Remover dos guardados" : "Guardar"} data-testid={testid || `fav-${type}-${id}`}
            className={`inline-flex items-center gap-1 rounded-full transition-colors ${size === "lg" ? "px-3 py-2 text-xs font-semibold border border-zinc-300 hover:border-black" : "p-1.5"} ${on ? "text-[#c8102e]" : "text-zinc-500 hover:text-black"} ${className}`}>
      <Heart size={px} fill={on ? "currentColor" : "none"} className="transition-transform active:scale-125" />
      {size === "lg" && <span>{on ? "Guardado" : "Guardar"}</span>}
      {n !== null && n > 0 && <span className="text-[10px] font-semibold" data-testid={`fav-count-${id}`}>{n}</span>}
    </button>
  );
}
